import { ErrorMessage, useFormikContext } from "formik"
import { useMemo } from "react"
import { Col, Row } from "react-bootstrap"
import { useTranslation } from "react-i18next"

import DatePicker from "@/components/Inputs/DateInput/DatePicker/DatePicker"
import SelectField from "@/components/Inputs/SelectField"
import TextInput from "@/components/Inputs/TextInput"
import useFormErr from "@/hooks/useFormErr"
import {
  useArabCountriesData,
  useCollegesData,
  useGovernoratesData,
  useProfessionsData,
} from "@/queries/queries"
import type { Option } from "@/types"
import type {
  ArabCountry,
  College,
  Governorate,
  Professsion,
} from "@/types/ProfileInfoTypes"
import type { StudentProfile, StudentWithPassword } from "@/types/profileTypes"

export default function UserProfileForm() {
  const { t, i18n } = useTranslation()
  const { values, setFieldValue } = useFormikContext<StudentWithPassword>()
  const { formErr } = useFormErr<StudentWithPassword>()

  const army_statuses = [
    { label: t("profilePage.army_statuses.postponed"), value: "postponed" },
    { label: t("profilePage.army_statuses.finished"), value: "finished" },
    { label: t("profilePage.army_statuses.in_progress"), value: "in_progress" },
    { label: t("profilePage.army_statuses.exempt"), value: "exempt" },
  ]

  const { data: professions } = useProfessionsData()
  const { data: arabCountries } = useArabCountriesData()
  const { data: governorate } = useGovernoratesData(
    arabCountries &&
      arabCountries.filter((ele) => ele.name === values.country)[0]?.id
  )
  const { data: colleges } = useCollegesData()

  const {
    arabCountriesOptions,
    collegesOptions,
    governorateOptions,
    professionsOptions,
  } = useProfileInfoData({
    arabCountries,
    governorate,
    colleges,
    professions,
    values,
    isArabic: i18n.language === "ar",
  })

  return (
    <>
      <Row className="">
        <Col lg={6}>
          <TextInput
            label={t("profilePage.data.name.label")}
            placeholder={t("profilePage.data.name.placeholder")}
            id="student_name"
            name="name"
            className={"mb-3"}
            as="field"
            backError={formErr?.name}
            error={<ErrorMessage name="name" />}
          />
        </Col>
        <Col lg={6}>
          <TextInput
            disabled
            label={t("profilePage.data.email.label")}
            placeholder={t("profilePage.data.email.placeholder")}
            id="email"
            className={"mb-3"}
            name="email"
            as="field"
            backError={formErr?.email}
            error={<ErrorMessage name="email" />}
          />
        </Col>
        <Col lg={6}>
          <TextInput
            label={t("profilePage.data.phone.label")}
            placeholder={t("profilePage.data.phone.placeholder")}
            id="supervisore_phone"
            name="phone"
            as="field"
            type={"text"}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value
              if (/^\d*$/.test(value)) {
                setFieldValue("phone", value)
              }
            }}
            backError={formErr?.phone}
            error={<ErrorMessage name="phone" />}
          />
        </Col>

        <Col lg={6}>
          <TextInput
            label={t("profilePage.data.password.label")}
            placeholder={t("profilePage.data.password.placeholder")}
            id="student_password"
            name="password"
            as="field"
            type={"password"}
            backError={formErr?.password}
            error={<ErrorMessage name="password" />}
          />
        </Col>
        <Col md={6} className="mb-3">
          <DatePicker
            openToDate={new Date("2010-02-09")}
            label={t("profilePage.data.birthdate.label")}
            placeholder={t("profilePage.data.birthdate.placeholder")}
            type="date"
            name="birthdate"
            className="label-fit w-100"
            id={"birthdate"}
            backError={formErr?.birthdate}
            error={<ErrorMessage name="birthdate" />}
          />
        </Col>
        <Col md={6} className="mb-4">
          <SelectField
            title={t("profilePage.data.country.label")}
            placeholder={t("profilePage.data.country.placeholder")}
            name="country"
            options={arabCountriesOptions}
            className="label-fit w-100"
            id={"country"}
            error={<ErrorMessage name="country" />}
            backError={formErr?.country}
          />
        </Col>
        <Col md={6} className="mb-4">
          <SelectField
            title={t("profilePage.data.city.label")}
            placeholder={t("profilePage.data.city.placeholder")}
            disabled={values?.country === ""}
            name="gov"
            options={governorateOptions}
            className="label-fit w-100"
            id={"gov"}
            error={<ErrorMessage name="gov" />}
            backError={formErr?.gov}
          />
        </Col>
        <Col md={6} className="mb-4">
          <SelectField
            title={t("profilePage.data.college.label")}
            placeholder={t("profilePage.data.college.placeholder")}
            disabled={values?.country === ""}
            name="college"
            options={collegesOptions}
            className="label-fit w-100"
            id={"college"}
            error={<ErrorMessage name="college" />}
            backError={formErr?.college}
          />
        </Col>
        <Col md={6} className="mb-4">
          <SelectField
            title={t("profilePage.data.profession.label")}
            placeholder={t("profilePage.data.profession.placeholder")}
            name="profession_id"
            options={professionsOptions}
            className="label-fit w-100"
            id={"profession_id"}
            error={<ErrorMessage name="profession_id" />}
            backError={formErr?.profession_id}
          />
        </Col>
        <Col md={6} className="mb-4">
          <SelectField
            onCreateOption={(newOption) =>
              setFieldValue("college", newOption.value)
            }
            title={t("profilePage.data.type.label")}
            placeholder={t("profilePage.data.type.placeholder")}
            name="student_status"
            options={[
              {
                value: 0,
                label: "student / طالب",
              },
              {
                value: 1,
                label: "graduate / خريج",
              },
            ]}
            className="label-fit w-100"
            id={"student_status"}
            error={<ErrorMessage name="student_status" />}
            backError={formErr?.student_status}
          />
        </Col>
        {values?.gender != undefined && Number(values?.gender) === 0 ? (
          <Col md={6} className="mb-4">
            <SelectField
              onCreateOption={(newOption) =>
                setFieldValue("army_status", newOption.value)
              }
              title={t("profilePage.data.army_status.label")}
              placeholder={t("profilePage.data.army_status.placeholder")}
              name="army_status"
              options={army_statuses}
              className="label-fit w-100"
              id={"army_status"}
              error={<ErrorMessage name="army_status" />}
              backError={formErr?.army_status}
            />
          </Col>
        ) : null}
      </Row>
    </>
  )
}

function useProfileInfoData({
  professions,
  governorate,
  colleges,
  arabCountries,
  values,
  isArabic,
}: {
  professions?: Array<Professsion>
  governorate?: Array<Governorate>
  colleges?: Array<College>
  arabCountries?: Array<ArabCountry>
  values: StudentProfile
  isArabic: boolean
}) {
  const { t } = useTranslation()

  const professionsOptions = useMemo(() => {
    if (!professions) return [] as Array<Option>
    return professions.map((ele) => ({
      label: (isArabic ? ele.name_ar : ele.name) || "",
      value: ele.id,
    }))
  }, [professions, isArabic])

  const governorateOptions = useMemo(() => {
    if (values.country !== "Egypt")
      return [
        {
          label: t("profilePage.defaults.other"),
          value: "other",
        },
      ]
    if (!governorate) return [] as Array<Option>
    return governorate.map((ele) => ({
      label: (isArabic ? ele.name_ar : ele.name) || "",
      value: ele.name || "0",
    }))
  }, [governorate, isArabic])

  const collegesOptions = useMemo(() => {
    if (values.country !== "Egypt")
      return [
        {
          label: t("profilePage.defaults.other"),
          value: "other",
        },
      ]
    if (!colleges) return [] as Array<Option>
    return colleges
      .map((ele) => ({
        label: (isArabic ? ele.name_ar : ele.name) || "",
        value: ele.name || "0",
      }))
      .concat([
        {
          label: t("profilePage.defaults.other"),
          value: "other",
        },
      ])
  }, [colleges, values.country, isArabic])

  const arabCountriesOptions = useMemo(() => {
    if (!arabCountries) return [] as Array<Option>
    return arabCountries
      .map((ele) => ({
        label: (isArabic ? ele.name_ar : ele.name) || "",
        value: ele.name || "0",
      }))
      .concat([
        {
          label: t("profilePage.defaults.other"),
          value: "other",
        },
      ])
  }, [arabCountries, isArabic])

  return {
    professionsOptions,
    governorateOptions,
    collegesOptions,
    arabCountriesOptions,
  }
}
